import React, { lazy, useContext, useEffect, useState } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { AuthProvider, RequireAuth, getCustomData } from 'shared/components/App/Auth'
import Eula from 'pages/Eula'
import NotFound from 'shared/pages/NotFound'
import NotFoundLoggedIn from 'pages/NotFoundLoggedIn'
import Page from 'shared/types/Page'
import Footer from 'shared/components/Footer'
import { loginViewModes, roles } from 'consts'
import ChooseCustomer from 'pages/ChooseCustomer'
import ResetPasswordRequest from 'pages/ResetPasswordRequest'
import ContactUs from 'pages/ContactUs'
import SetNewPassword from 'pages/SetNewPassword'
import useInit from './hooks/useInit'
import useUpdateMenu from './hooks/useUpdateMenu'
import { ReactReduxContext } from 'react-redux'
import MenuContext from '../../shared/components/AppRouter/context/MenuContext'
import { setLastUrl } from 'shared/redux/actions'
import { useNavigate } from "react-router-dom"

const Login = lazy(() => import('shared/containers/Login'))

interface AppRouterProps {}

const AppRouter: React.FunctionComponent<AppRouterProps> = () => {
  const [allPages, setAllPages] = useState<Page[]>([])
  const [viewMode, setViewMode] = useState<any>(loginViewModes.login)
  const [viewModeLoaded, setViewModeLoaded] = useState<boolean>(false)
  const { store } = useContext(ReactReduxContext)
  const menuPages = store.getState().menuPages

  const customer = getCustomData('customer') ?? false
  const eulaAccepted = getCustomData('eulaAccepted') ?? false
  const role = getCustomData('role')
  const identity = store.getState().identity
  const isAuthenticated = identity?.username !== '' && identity?.token !== false
  const isUserPendingCustomerSelection = isAuthenticated && !customer && role === roles.user
  const navigate = useNavigate()
  
  const curLoc = useLocation()
  useInit({
    setPages: setAllPages,
    pages: allPages,
    menuPages,
    role: getCustomData('role'),
    customer,
    setViewModeLoaded
  })

  useUpdateMenu({
    setPages: setAllPages,
    pages: allPages,
    menuPages,
    role: getCustomData('role'),
    customer: getCustomData('customer') ?? false
  })

  // init last url state
  useEffect(() => {

    let lastUrl = store.getState()?.lastUrl.lastUrl
    if (lastUrl === null) {
      lastUrl = {}
    }

    if (typeof lastUrl['user'] === 'undefined') {
      allPages.forEach((item) => {
        if (item.roles?.includes('user') && item?.path && (typeof item?.path === 'string') &&
          (typeof lastUrl['user'] === 'undefined')) {
          lastUrl['user'] = item.path
        }
      })
    }

    if (typeof lastUrl['super admin'] === 'undefined') {
      allPages.forEach((item) => {
        if (item.roles?.includes('super admin') && item?.path && (typeof item?.path === 'string') &&
          (typeof lastUrl['super admin'] === 'undefined')) {
          lastUrl['super admin'] = item.path
        }
      })
    }

    // navigate if invalid url
    const viewMode = store.getState().viewMode?.mode
    if (allPages.length && ((curLoc.pathname === '/') || (curLoc.pathname === ''))) {
      const page = allPages.find(item => {
        return item?.path && item.path.length &&
          item?.roles && item.roles.includes(viewMode)
      })

      if (typeof page !== 'undefined' && typeof page.path === 'string') {
        navigate(page.path)
      }
    }  
    
  }, [allPages])

  // update last url upon navigation
  useEffect(() => {

    const path = window.location.pathname
    // console.log(getAllCustomData())
    if (viewModeLoaded && (path !== '/')) {
      // log last location for view mode
      const viewMode = store.getState().viewMode

      if (viewMode.mode !== false) {
        let lastUrl = store.getState()?.lastUrl.lastUrl
        
        if (lastUrl === null) {
          lastUrl = {}
        }

        lastUrl[viewMode.mode] = path
        store.dispatch(setLastUrl(lastUrl))
      }
    }
  }, [curLoc])

  const FooterContent = () => {

    return (
      <>
        <div className='d-flex justify-content-center'>
          <hr className='w-25' />
        </div>
        {viewMode !== loginViewModes.login && (
          <div className='text-center small'>
            <a
              href='#'
              onClick={(e) => {
                e.preventDefault()
                setViewMode(loginViewModes.login)
              }}
            >
              Login
            </a>
          </div>
        )}
        {![loginViewModes.resetPassword, loginViewModes.setNewPassword].includes(viewMode) && (
          <div className='text-center small'>
            <a
              href='#'
              onClick={(e) => {
                e.preventDefault()
                setViewMode(loginViewModes.resetPassword)
              }}
            >
              Can't access your account?
            </a>
          </div>
        )}
        {viewMode !== loginViewModes.contactUs && (
          <div className='text-center small'>
            <a
              href='#'
              onClick={(e) => {
                e.preventDefault()
                setViewMode(loginViewModes.contactUs)
              }}
            >
              Contact Us
            </a>
          </div>
        )}
        <Footer />
      </>
    )
  }

  const PostLoginFormContent = () => {
    return (
      <>
        {viewMode === loginViewModes.resetPassword && (
          <ResetPasswordRequest
            usernameFieldLabel='Email'
            usernameFieldName='email'
            setViewMode={setViewMode}
          />
        )}
        {viewMode === loginViewModes.setNewPassword && (
          <SetNewPassword
            loginRoute='/login'
            usernameFieldLabel='Email'
            usernameFieldName='email'
            setViewMode={setViewMode}
          />
        )}
        {viewMode === loginViewModes.contactUs && (
          <ContactUs setViewMode={setViewMode} />
        )}
      </>
    )
  }

  //do not create the login component as a function; otherwise, the state passed to the component will not work as expected: navigate('/login', { replace: true, state: { expired: true } })
  const LoginComponent = <Login
      currentViewMode={viewMode}
      setViewMode={setViewMode}
      loginViewMode={loginViewModes.login}
      footerContent={<FooterContent />}
      postLoginFormContent={<PostLoginFormContent />}
      usernameFieldLabel='Email'
      usernameFieldName='email'
      showRememberMe={false}
      onLoaded={(props, params, navigate) => {
        if (params.hasOwnProperty('token')) {
          props.setViewMode(loginViewModes.setNewPassword)
        }
      }}
    />

  return (
    <AuthProvider>
      <MenuContext.Provider value={allPages}>
        <Routes>
          {eulaAccepted && allPages.length && !isUserPendingCustomerSelection && allPages.map((props: any, key: number | string) => {
            if (props.path) {

              const page = React.cloneElement(<props.component key={props.name} />)

              return (
                <Route
                  key={props.name}
                  path={props.path}
                  element={
                    <RequireAuth roles={props.roles}>
                      {page}
                    </RequireAuth>
                  }
                />
              )
            }
          })}
          {<Route
            path='/login/:token?'
            element={LoginComponent}
          />}
          {!customer && !isUserPendingCustomerSelection && <Route
            path='/*/:token?'
            element={LoginComponent}
          />}
          {isUserPendingCustomerSelection && (
            <Route path='/choose-customer' element={<ChooseCustomer />} />
          )}
          {isUserPendingCustomerSelection && (
            <Route path='*' element={<Navigate to='/choose-customer' replace />} />
          )}
          {!allPages.length && !isUserPendingCustomerSelection && <Route
            path={curLoc.pathname}
            element={<div id="page-loading-container"><img src="/images/loading.svg" alt="Application Loading..." /></div>}
          />}
          {!eulaAccepted && <Route path='*' element={<Eula />} />}
          {eulaAccepted && customer && <Route path='*' element={<NotFoundLoggedIn />} />}
          {eulaAccepted && !customer && <Route path='*' element={<NotFound />} />}
        </Routes>
      </MenuContext.Provider>
    </AuthProvider>
  )
}

export default AppRouter
